import React from 'react';
import { Search, Filter, Download } from 'lucide-react';
import { useContractStore } from '../../stores/contractStore';
import { toast } from 'react-hot-toast';

const filterOptions = [
  {
    id: 'status',
    name: 'Status',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
      { value: 'archived', label: 'Archived' },
    ],
  },
  {
    id: 'agency',
    name: 'Agency',
    options: [
      { value: 'dod', label: 'Department of Defense' },
      { value: 'dhs', label: 'Department of Homeland Security' },
      { value: 'doe', label: 'Department of Energy' },
    ],
  },
  {
    id: 'setAside',
    name: 'Set-Aside',
    options: [
      { value: 'small-business', label: 'Small Business' },
      { value: '8a', label: '8(a)' },
      { value: 'sdvosb', label: 'SDVOSB' },
      { value: 'wosb', label: 'WOSB' },
    ],
  },
];

interface ContractFiltersProps {
  onSearch: (query: string) => void;
}

export function ContractFilters({ onSearch }: ContractFiltersProps) {
  const { setFilters, exportContracts } = useContractStore();

  const handleFilterChange = (filterId: string, value: string) => {
    setFilters({ [filterId]: value });
  };

  const handleExport = async (format: 'csv' | 'pdf') => {
    try {
      await exportContracts(format);
      toast.success(`Contracts exported successfully as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to export contracts');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search contracts..."
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handleExport('csv')}
            className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </button>
          <button
            onClick={() => handleExport('pdf')}
            className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <Download className="mr-2 h-4 w-4" />
            Export PDF
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        {filterOptions.map((filter) => (
          <div key={filter.id} className="min-w-48">
            <label htmlFor={filter.id} className="block text-sm font-medium text-gray-700">
              {filter.name}
            </label>
            <select
              id={filter.id}
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
              onChange={(e) => handleFilterChange(filter.id, e.target.value)}
              defaultValue=""
            >
              <option value="">All</option>
              {filter.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}